<script setup>
import { ref, computed } from "vue";
import { useAuthStore } from "@/stores/auth-store";

const authStore = useAuthStore();

const props = defineProps({
  name: {
    type: String,
    default: "Jhon doe",
  },
  course: {
    type: String,
    default: "Web Development Mastery Course",
  },
  date: {
    type: String,
    default: "August 13, 2025",
  },
  instructor: {
    type: String,
    default: "Admin",
  },
  company: {
    type: [String, Object],
    default: "",
  },
});

const certRef = ref(null);

const companyLogo = computed(() => {
  let raw =
    props.company ||
    authStore.currentUser?.company ||
    authStore.currentUser?.cmp_code ||
    "";
  if (typeof raw === "object" && raw !== null) {
    raw = raw.cmp_code || raw.code || raw.name || "";
  }
  const code = String(raw).trim().toUpperCase();

  if (code === "SIS" || code.includes("SMARTINDO")) {
    return "/smartindo.png";
  }
  if (code === "ATI" || code.includes("ACECOM")) {
    return "/acecom.png";
  }
  if (code === "SSI" || code.includes("SMARTPRO")) {
    return "/smartpro.png";
  }
  if (code === "STT" || code.includes("SAPTA")) {
    return "/sapta.png";
  }

  return "/sapta.png";
});

// Biar parent bisa akses
defineExpose({ certRef });
</script>

<template>
  <div class="certificate" ref="certRef">
    <div class="decorative-border"></div>
    <div class="decorative-corners corner-tl"></div>
    <div class="decorative-corners corner-tr"></div>
    <div class="decorative-corners corner-bl"></div>
    <div class="decorative-corners corner-br"></div>

    <div class="logo">
      <div class="logo-container">
        <img :src="companyLogo" alt="Company Logo" />
      </div>
    </div>

    <!-- <h1 class="certificate-title">Certificate of Achievement</h1> -->
    <div class="certificate-title">
      <svg viewBox="0 0 800 80" xmlns="http://www.w3.org/2000/svg" class="gradient-title">
        <defs>
          <linearGradient id="titleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stop-color="#fcb816" />
            <stop offset="100%" stop-color="#1e90ff" />
          </linearGradient>
        </defs>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">
          Certificate of Achievement
        </text>
      </svg>
    </div>

    <p class="subtitle">This is to certify that</p>

    <div class="recipient-section">
      <p class="awarded-to">This certificate is proudly awarded to</p>
      <h2 class="recipient-name">{{ name }}</h2>
      <p class="achievement">
        For outstanding performance and dedication in completing the
        <strong>{{ course }}</strong> with exceptional skill and commitment to
        excellence.
      </p>
    </div>

    <div class="details">
      <div class="date">
        <p class="date-label">Date Awarded</p>
        <p class="date-value">{{ date }}</p>
      </div>

      <div class="signature">
        <p class="signature-label">Authorized Signature</p>
        <div class="signature-line"></div>
        <p class="signature-name">{{ instructor }}</p>
        <!-- <p class="signature-title">Director of Education</p> -->
      </div>
    </div>

    <!-- <div class="certificate-id">
                Certificate ID: JD-2025-001234
            </div> -->
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Open+Sans:wght@300;400;600&display=swap");

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: "Open Sans", sans-serif;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.certificate {
  width: 100%;
  max-width: 800px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 60px 40px;
  position: relative;
  overflow: hidden;
}

.certificate::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(90deg, #fcb816, #1e90ff);
}

.certificate::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(90deg, #1e90ff, #fcb816);
}

.decorative-border {
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  bottom: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 15px;
  pointer-events: none;
}

.decorative-corners {
  position: absolute;
  width: 40px;
  height: 40px;
}

.corner-tl {
  top: 10px;
  left: 10px;
  border-top: 3px solid #fcb816;
  border-left: 3px solid #fcb816;
  border-radius: 10px 0 0 0;
}

.corner-tr {
  top: 10px;
  right: 10px;
  border-top: 3px solid #1e90ff;
  border-right: 3px solid #1e90ff;
  border-radius: 0 10px 0 0;
}

.corner-bl {
  bottom: 10px;
  left: 10px;
  border-bottom: 3px solid #1e90ff;
  border-left: 3px solid #1e90ff;
  border-radius: 0 0 0 10px;
}

.corner-br {
  bottom: 10px;
  right: 10px;
  border-bottom: 3px solid #fcb816;
  border-right: 3px solid #fcb816;
  border-radius: 0 0 10px 0;
}

.logo {
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
}

.logo-container {
  max-width: 250px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.logo-container::before {
  content: "";
  position: absolute;
  width: 120%;
  height: 120%;
  background: radial-gradient(circle,
      rgba(255, 255, 255, 0.2) 0%,
      transparent 70%);
  top: -10%;
  left: -10%;
}

.logo-container img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* .certificate-title {
            text-align: center;
            font-family: 'Playfair Display', serif;
            font-size: 36px;
            font-weight: 700;
            margin-bottom: 10px;
            background: linear-gradient(45deg, #fcb816, #1e90ff);
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
        } */

.certificate-title {
  text-align: center;
  margin-bottom: 10px;
}

.gradient-title {
  width: 100%;
  height: 80px;
}

.gradient-title text {
  font-family: "Playfair Display", serif;
  font-size: 36px;
  font-weight: 700;
  fill: url(#titleGradient);
}

.subtitle {
  text-align: center;
  font-size: 16px;
  color: #7f8c8d;
  margin-bottom: 40px;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.recipient-section {
  text-align: center;
  margin-bottom: 40px;
}

.awarded-to {
  font-size: 18px;
  color: #34495e;
  margin-bottom: 15px;
}

.recipient-name {
  font-family: "Playfair Display", serif;
  font-size: 42px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  position: relative;
}

.recipient-name::after {
  content: "";
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 3px;
  background: linear-gradient(90deg, #fcb816, #1e90ff);
  border-radius: 2px;
}

.achievement {
  font-size: 18px;
  color: #34495e;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto 40px;
  text-align: center;
}

.details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 60px;
}

.date,
.signature {
  text-align: center;
}

.date-label,
.signature-label {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.date-value {
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
}

.signature-line {
  width: 200px;
  height: 2px;
  background: linear-gradient(90deg, #fcb816, #1e90ff);
  margin: 0 auto 10px;
  border-radius: 1px;
}

.signature-name {
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
}

.signature-title {
  font-size: 14px;
  color: #7f8c8d;
  margin-top: 5px;
}

.certificate-id {
  position: absolute;
  bottom: 20px;
  right: 30px;
  font-size: 12px;
  color: #bdc3c7;
  font-family: "Courier New", monospace;
}

@media print {
  body {
    background: white;
    padding: 0;
  }

  .certificate {
    box-shadow: none;
    max-width: none;
    width: 100%;
  }

  .certificate-title {
    background: none !important;
    -webkit-background-clip: initial !important;
    background-clip: initial !important;
    -webkit-text-fill-color: initial !important;
    color: #fcb816 !important;
  }
}

@media (max-width: 768px) {
  .certificate {
    padding: 40px 20px;
  }

  .certificate-title {
    font-size: 28px;
  }

  .recipient-name {
    font-size: 32px;
  }

  .details {
    flex-direction: column;
    gap: 30px;
  }

  .signature-line {
    width: 150px;
  }
}
</style>
